import { Box, Card, CardContent, Typography, IconButton } from "@mui/material";
import { Event } from "@root/utils/entities";
import moment from "moment";
import { useMemo, useState } from "react";
import { StyledChip } from "./common/styled";
import {
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import followButton from "@images/follow.svg";
import followingButton from "@images/following.svg";
import hideButton from "@images/hide.svg";
import unhideButton from "@images/unhide.svg";
import shareIcon from "@images/share.svg";
import { useAccountContext } from "@root/stores/accountStore";
import { userService } from "@root/services/api/userService";
import { getApiError, hasErrors, formatEventDateRange } from "@root/utils/functions";
import { EventPin } from "@root/components/common/EventPin";
import { ImagePreview } from "@root/components/common/ImagePreview";

interface EventItemProps {
  event: Event;
  onView: () => void;
  onFollowChange?: (isFollowed: boolean) => void;
  onHideChange?: (eventId: string, isHidden: boolean) => void;
  showHideIcon?: boolean;
  showFollowIcon?: boolean;
  showShareIcon?: boolean;
  onShare?: () => void;
}
export const EventListItem = ({
  event,
  onView,
  onFollowChange,
  onHideChange,
  showHideIcon = true,
  showFollowIcon = true,
  showShareIcon = false,
  onShare,
}: EventItemProps) => {
  const accountStore = useAccountContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const [optimisticFollowState, setOptimisticFollowState] = useState<
    boolean | null
  >(null);
  const [optimisticHideState, setOptimisticHideState] = useState<
    boolean | null
  >(null);

  // Read directly from event object as source of truth
  // Use optimistic state only during API calls
  const currentFollowState =
    optimisticFollowState !== null
      ? optimisticFollowState
      : event.isFollowed || false;

  const currentHideState =
    optimisticHideState !== null
      ? optimisticHideState
      : event.isHidden || false;

  const start = useMemo(
    () => moment(event.dateRange?.start),
    [event.dateRange?.start]
  );
  const end = useMemo(
    () => moment(event.dateRange?.end),
    [event.dateRange?.end]
  );

  const handleFollowClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!accountStore.account) {
      accountStore.authModalStore.openAuthModal("login");
      return;
    }

    setIsLoading(true);

    // Optimistic update - immediately show the new state
    const currentState = event.isFollowed || false;
    const newOptimisticState = !currentState;
    setOptimisticFollowState(newOptimisticState);

    try {
      // Call the appropriate endpoint based on current follow state
      const response = currentState
        ? await userService.unfollowEvent(accountStore.account.id, event.id)
        : await userService.followEvent(accountStore.account.id, event.id);

      if (hasErrors(response)) {
        console.error(
          "Failed to follow/unfollow event:",
          getApiError(response).message
        );
        // Revert optimistic update on error
        setOptimisticFollowState(null);
      } else {
        // Update event object and clear optimistic state
        event.isFollowed = newOptimisticState;
        setOptimisticFollowState(null);
        onFollowChange?.(newOptimisticState);

        // Verify with backend
        if (accountStore.account?.id) {
          userService
            .getFollowedEvents(accountStore.account.id)
            .then((followedResponse) => {
              if (!hasErrors(followedResponse)) {
                const followedEventIds = new Set(
                  followedResponse.data?.events?.map((evt) => evt.id) ?? []
                );
                const verifiedFollowState = followedEventIds.has(event.id);
                event.isFollowed = verifiedFollowState;
                onFollowChange?.(verifiedFollowState);
              }
            });
        }
      }
    } catch (error) {
      console.error("Error following/unfollowing event:", error);
      // Revert optimistic update on error
      setOptimisticFollowState(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHideClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!accountStore.account) {
      accountStore.authModalStore.openAuthModal("login");
      return;
    }

    setIsHiding(true);

    // Optimistic update - immediately show the new state
    const currentState = event.isHidden || false;
    const newOptimisticState = !currentState;
    setOptimisticHideState(newOptimisticState);

    try {
      // Call the appropriate endpoint based on current hide state
      const response = currentState
        ? await userService.unhideEvent(accountStore.account.id, event.id)
        : await userService.hideEvent(accountStore.account.id, event.id);

      if (hasErrors(response)) {
        console.error(
          "Failed to hide/unhide event:",
          getApiError(response).message
        );
        // Revert optimistic update on error
        setOptimisticHideState(null);
      } else {
        // Update event object and clear optimistic state
        event.isHidden = newOptimisticState;
        setOptimisticHideState(null);

        // Notify parent component of the hide state change
        if (onHideChange) {
          onHideChange(event.id, newOptimisticState);
        }
      }
    } catch (error) {
      console.error("Error hiding/unhiding event:", error);
      // Revert optimistic update on error
      setOptimisticHideState(null);
    } finally {
      setIsHiding(false);
    }
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onShare) {
      onShare();
      return;
    }

    // Construct the share URL (adjust based on your app's actual URL structure)
    // Assuming /event/:id is the route
    const shareUrl = `${window.location.origin}/event/${event.id}`;
    const shareData = {
      title: event.name,
      text: `Check out this event: ${event.name}`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        // Simple feedback since no toast/snackbar is available
        alert("Link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  // Helper to check if image URL is valid (not empty, not __REMOVE__ marker)
  // Backend bug: returns S3 URL with __REMOVE__ as filename instead of null
  const isValidImageUrl = (url?: string | null): boolean => {
    if (!url || url.trim() === "") return false;
    if (url.includes("__REMOVE__")) return false;
    return true;
  };

  const hasImage = isValidImageUrl(event.bannerImageUrl);

  return (
    <Card
      onClick={onView}
      sx={{
        borderRadius: 0,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid #e0e0e0",
        transition: "all 0.2s ease-in-out",
        borderColor: event.pinHue || "#FF0000",
        cursor: "pointer",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          backgroundColor: "#f4f8fb",
        },
        mb: 0.5,
        position: "relative",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Hide/Unhide button - top right of card in desktop */}
      {showHideIcon && (
        <IconButton
          size="small"
          onClick={handleHideClick}
          disabled={isHiding}
          sx={{
            position: "absolute",
            top: { xs: "auto", sm: 12 },
            right: { xs: "auto", md: 52 },
            display: { xs: "none", md: "flex" },
            backgroundColor: "rgba(255,255,255,0.9)",
            color: currentHideState ? "#4CAF50" : "#666",
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 },
            zIndex: 10,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              color: "#ff0000",
              transform: "scale(1.1)",
              borderColor: "#ff0000",
              "& svg": {
                color: "#ff0000",
              },
            },
            "&:disabled": {
              opacity: 0.6,
            },
          }}
        >
          <Box
            component="img"
            src={currentHideState ? unhideButton : hideButton}
            alt={currentHideState ? "Unhide" : "Hide"}
            sx={{
              width: { xs: "1rem", sm: "1.1rem" },
              height: { xs: "1rem", sm: "1.1rem" },
              color: "inherit",
            }}
          />
        </IconButton>
      )}

      {/* Like button - top right of card in desktop */}
      {showFollowIcon && (
        <IconButton
          size="small"
          onClick={handleFollowClick}
          disabled={isLoading}
          sx={{
            position: "absolute",
            top: { xs: "auto", sm: 12 },
            right: { xs: "auto", md: 12 },
            display: { xs: "none", md: "flex" },
            backgroundColor: "rgba(255,255,255,0.9)",
            color: currentFollowState ? "#ff4757" : "#666",
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 },
            zIndex: 10,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              color: "#ff4757",
              transform: "scale(1.1)",
            },
            "&:disabled": {
              opacity: 0.6,
            },
          }}
        >
          <Box
            component="img"
            src={currentFollowState ? followingButton : followButton}
            alt={currentFollowState ? "Following" : "Follow"}
            sx={{
              width: { xs: "1rem", sm: "1.1rem" },
              height: { xs: "1rem", sm: "1.1rem" },
            }}
          />
        </IconButton>
      )}

      {/* Share button - top right of card in desktop (replaces follow if enabled) */}
      {showShareIcon && !showFollowIcon && (
        <IconButton
          size="small"
          onClick={handleShareClick}
          sx={{
            position: "absolute",
            top: { xs: "auto", sm: 12 },
            right: { xs: "auto", md: 12 },
            display: { xs: "none", md: "flex" },
            backgroundColor: "rgba(255,255,255,0.9)",
            color: "#666",
            width: { xs: 28, sm: 32 },
            height: 28, // consistent height
            zIndex: 10,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              transform: "scale(1.1)",
            },
          }}
        >
          <Box
            component="img"
            src={shareIcon}
            alt="Share"
            sx={{
              width: { xs: "1rem", sm: "1.1rem" },
              height: { xs: "1rem", sm: "1.1rem" },
            }}
          />
        </IconButton>
      )}

      <CardContent
        sx={{
          padding: { xs: "0.75rem !important", md: "1rem !important" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Event Image with overlays */}
          {hasImage && (
            <Box
              sx={{
                width: { xs: "100%", md: 200 },
                height: { xs: 160, md: 170 }, // Fixed height for desktop to maintain aspect ratio
                minHeight: { xs: 160, md: 170 },
                flexShrink: 0,
                position: "relative",
                border: "1px solid #e0e0e0",
                overflow: "hidden",
                borderRadius: "0",
                marginBottom: { xs: "0.5rem", md: 0 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ImagePreview
                src={event.bannerImageUrl}
                alt={event.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />

              {/* Hide/Unhide button - top right of image in mobile view only */}
              {showHideIcon && (
                <IconButton
                  size="small"
                  onClick={handleHideClick}
                  disabled={isHiding}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 38,
                    display: { xs: "flex", md: "none" },
                    backgroundColor: "rgba(255,255,255,0.9)",
                    color: currentHideState ? "#4CAF50" : "#666",
                    width: 28,
                    height: 28,
                    zIndex: 3,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#fff",
                      transform: "scale(1.1)",
                    },
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <Box
                    component="img"
                    src={currentHideState ? unhideButton : hideButton}
                    alt={currentHideState ? "Unhide" : "Hide"}
                    sx={{ width: "1.1rem", height: "1.1rem" }}
                  />
                </IconButton>
              )}

              {/* Like button - top right of image in mobile view only (next to hide) */}
              {showFollowIcon && (
                <IconButton
                  size="small"
                  onClick={handleFollowClick}
                  disabled={isLoading}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    display: { xs: "flex", md: "none" },
                    backgroundColor: "rgba(255,255,255,0.9)",
                    color: currentFollowState ? "#ff4757" : "#666",
                    width: 28,
                    height: 28,
                    zIndex: 3,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,1)",
                      color: "#ff4757",
                      transform: "scale(1.1)",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={currentFollowState ? followingButton : followButton}
                    alt={currentFollowState ? "Following" : "Follow"}
                    sx={{ width: "1rem", height: "1rem" }}
                  />
                </IconButton>
              )}

              {/* Share button - top right of image in mobile view only (replaces follow if enabled) */}
              {showShareIcon && !showFollowIcon && (
                <IconButton
                  size="small"
                  onClick={handleShareClick}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    display: { xs: "flex", md: "none" },
                    backgroundColor: "rgba(255,255,255,0.9)",
                    color: "#666",
                    width: 28,
                    height: 28,
                    zIndex: 3,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,1)",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={shareIcon}
                    alt="Share"
                    sx={{ width: "1rem", height: "1rem" }}
                  />
                </IconButton>
              )}
            </Box>
          )}

          {/* Event Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {/* Event Details */}
            <Box
              sx={{
                flex: 1,
                pb: { xs: 0.5, md: 0.75 },
              }}
            >
              {/* Event Title with Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: { xs: 0.5, md: 0.75 },
                  position: "relative",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                    lineHeight: 1.2,
                    color: "#1a1a1a",
                    pr: { xs: 0, md: 10 }, // Space for absolute icons on desktop
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: { xs: 2, sm: 1 },
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {event.name}
                </Typography>

                {/* Mobile Follow/Hide buttons - visible only when NO image */}
                {!hasImage && (
                  <Box
                    sx={{
                      display: { xs: "flex", md: "none" },
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {showHideIcon && (
                      <IconButton
                        size="small"
                        onClick={handleHideClick}
                        disabled={isHiding}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.9)",
                          color: currentHideState ? "#4CAF50" : "#666",
                          width: 28,
                          height: 28,
                          transition: "all 0.2s ease-in-out",
                          border: "1px solid #e0e0e0",
                          "&:hover": {
                            backgroundColor: "#fff",
                            transform: "scale(1.1)",
                          },
                          "&:disabled": {
                            opacity: 0.6,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={currentHideState ? unhideButton : hideButton}
                          alt={currentHideState ? "Unhide" : "Hide"}
                          sx={{ width: "1rem", height: "1rem" }}
                        />
                      </IconButton>
                    )}
                    {showFollowIcon && (
                      <IconButton
                        size="small"
                        onClick={handleFollowClick}
                        disabled={isLoading}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.9)",
                          color: currentFollowState ? "#ff4757" : "#666",
                          width: 28,
                          height: 28,
                          transition: "all 0.2s ease-in-out",
                          border: "1px solid #e0e0e0",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,1)",
                            color: "#ff4757",
                            transform: "scale(1.1)",
                          },
                          "&:disabled": {
                            opacity: 0.6,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={currentFollowState ? followingButton : followButton}
                          alt={currentFollowState ? "Following" : "Follow"}
                          sx={{ width: "1rem", height: "1rem" }}
                        />
                      </IconButton>
                    )}
                    {showShareIcon && !showFollowIcon && (
                      <IconButton
                        size="small"
                        onClick={handleShareClick}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.9)",
                          color: "#666",
                          width: 28,
                          height: 28,
                          transition: "all 0.2s ease-in-out",
                          border: "1px solid #e0e0e0",
                          "&:hover": {
                            backgroundColor: "#fff",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={shareIcon}
                          alt="Share"
                          sx={{ width: "1rem", height: "1rem" }}
                        />
                      </IconButton>
                    )}

                  </Box>
                )}
              </Box>

              {/* Address with icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: { xs: 0.4, sm: 0.5 },
                  gap: 0.5,
                }}
              >
                <LocationOnIcon
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    color: "#666",
                    mt: 0.1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {(() => {
                    const addr = event.location?.address;
                    if (!addr) return "Location TBD";
                    const parts = [
                      addr.addressLine1,
                      addr.city,
                      addr.state,
                      addr.zip ? addr.zip.toUpperCase() : null,
                      addr.country ? (addr.country === "US" ? "United States" : addr.country === "CA" ? "Canada" : addr.country) : null
                    ].filter(Boolean);
                    return parts.length > 0 ? parts.join(", ") : (addr.textDisplay || "Location TBD");
                  })()}
                </Typography>
              </Box>

              {/* Venue Name */}
              {event.locationName && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1a1a1a",
                    fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    fontWeight: 500,
                    mb: { xs: 0.4, sm: 0.5 },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {event.locationName}
                </Typography>
              )}

              {(() => {
                const dateRange = formatEventDateRange(start, end);

                if (dateRange.isOneDay) {
                  return (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          lineHeight: 1.4,
                        }}
                      >
                        {dateRange.date}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          lineHeight: 1.4,
                        }}
                      >
                        {dateRange.time}
                      </Typography>
                    </>
                  );
                }

                return (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {dateRange.startDate}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#999",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {dateRange.startTime}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        lineHeight: 1.4,
                        mt: 0.3,
                      }}
                    >
                      {dateRange.endDate}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#999",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {dateRange.endTime}
                    </Typography>
                  </>
                );
              })()}
            </Box>

            {/* Event categories, pin, and details button row */}
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 1,
                mt: { xs: 0.5, md: 0.75 },
                flexShrink: 0,
                flexWrap: { xs: "nowrap", md: "nowrap" },
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                  flexWrap: "wrap",
                  flex: 1,
                  maxWidth: { xs: "calc(100% - 160px)", md: "auto" },
                }}
              >
                {event.categories?.slice(0, 3).map((category) => (
                  <StyledChip
                    key={`eventCategory-${event.id}-${category.id}`}
                    label={category.name}
                    sx={{
                      backgroundColor: "#f0efefff !important",
                      boxShadow: "none",
                      borderColor: "#1C1B1F",
                      color: "#666",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      height: "32px",
                      borderRadius: "16px",
                      maxWidth: "100%",
                    }}
                  />
                ))}
                {event.categories && event.categories.length > 3 && (
                  <StyledChip
                    label={`+${event.categories.length - 3} more`}
                    sx={{
                      backgroundColor: "#f0efefff !important",
                      boxShadow: "none",
                      borderColor: "#1C1B1F",
                      color: "#666",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      height: "32px",
                      borderRadius: "16px",
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexShrink: 0,
                  alignSelf: "center",
                  ml: "auto",
                }}
              >
                <EventPin
                  iconUrl={(event as any).iconUrl || event.IconUrl}
                  pinHue={event.pinHue}
                  size="small"
                  sx={{ alignSelf: "center", flexShrink: 0 }}
                />

                <StyledChip
                  variant="outlined"
                  label="Event Details"
                  sx={(theme) => ({
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    height: "36px",
                    borderRadius: "18px",
                    transition: "all 0.2s ease-in-out",
                    minWidth: { xs: "auto", sm: "auto" },
                    justifyContent: "center",
                    display: "flex",
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      borderColor: `${theme.palette.primary.main} !important`,
                      color: "white !important",
                      transform: "translateY(-1px)",
                      boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
                      "&&": {
                        backgroundColor: `${theme.palette.primary.main} !important`,
                      },
                    },
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onView();
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
